import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";

import type { EntryLine, Inward } from "../types";
import { ui } from "../lib/ui";
import {
  MediaFrameViewer,
  type FrameSize,
  type MediaFrameContext,
} from "./MediaFrameViewer";

/** Interactive entry-line editor.
 *
 *  The operator opens a sample clip (or a screenshot) from the branch camera
 *  and clicks the two ends of the doorway line; the picker converts screen
 *  pixels back to native frame pixels, which is what the pipeline's
 *  LineCounter expects. The sample's pixel size is reported alongside the
 *  line (`frameSize`): NVRs export the same camera at different resolutions,
 *  so the pipeline resizes footage to that size before counting. Opening a
 *  sample of another resolution rescales an already-drawn line to it.
 *
 *  The media handling (local object URL — a native NVR export the browser
 *  cannot decode is converted by the API host's ffmpeg and not kept —
 *  playback, frame-size detection) lives in the shared MediaFrameViewer;
 *  only the line's coordinates ever leave this component. */

const LABEL =
  "pointer-events-none fill-white font-sans font-semibold [paint-order:stroke] [stroke:rgba(0,0,0,0.8)]";

const INWARD: Inward[] = ["up", "down", "left", "right"];
const HANDLE_HIT_PX = 14; // screen pixels within which a click grabs an endpoint
const ARROW_LEN_FRAC = 0.08; // arrow length as a fraction of the frame's short side

type Point = [number, number];
type Endpoint = "start" | "end";

type FrameSizeTuple = [number, number];

interface LinePickerProps {
  value: EntryLine;
  /** Pixel size the line is expressed in — the last sample it was drawn on. */
  frameSize: FrameSizeTuple | null;
  /** Always carries the frame size the line is now expressed in. */
  onChange: (line: EntryLine, frameSize: FrameSizeTuple | null) => void;
}

function inwardVector(inward: Inward): Point {
  switch (inward) {
    case "up":
      return [0, -1];
    case "down":
      return [0, 1];
    case "left":
      return [-1, 0];
    case "right":
      return [1, 0];
  }
}

function clampPoint(p: Point, frame: FrameSize): Point {
  return [
    Math.min(Math.max(Math.round(p[0]), 0), frame.width),
    Math.min(Math.max(Math.round(p[1]), 0), frame.height),
  ];
}

const isDrawn = (line: EntryLine) =>
  line.start[0] !== line.end[0] || line.start[1] !== line.end[1];

const sameSize = (a: FrameSizeTuple | null, b: FrameSizeTuple) =>
  a !== null && a[0] === b[0] && a[1] === b[1];

export function LinePicker({ value, frameSize, onChange }: LinePickerProps) {
  // Mirror of the opened sample's size (the viewer owns the media itself).
  const [frame, setFrame] = useState<FrameSize | null>(null);
  // Set when opening this sample rescaled a line drawn on a different size.
  const [rescaledFrom, setRescaledFrom] = useState<FrameSizeTuple | null>(null);
  const [next, setNext] = useState<Endpoint>("start");
  const [dragging, setDragging] = useState<Endpoint | null>(null);
  const [hover, setHover] = useState<Point | null>(null);
  // Endpoints that have been placed — a saved line counts as both placed; a
  // blank line (0,0 → 0,0) has neither, so nothing is drawn until the user clicks.
  const [placed, setPlaced] = useState<Set<Endpoint>>(() =>
    isDrawn(value) ? new Set<Endpoint>(["start", "end"]) : new Set<Endpoint>(),
  );

  // A non-degenerate line (saved, or typed into the numeric fields) counts as
  // fully placed, so it draws as soon as a frame is loaded.
  const internalEdit = useRef(false);
  useEffect(() => {
    if (internalEdit.current) {
      // Change came from a click/drag in this picker — placement is tracked there.
      internalEdit.current = false;
      return;
    }
    if (isDrawn(value)) {
      setPlaced((prev) =>
        prev.size === 2 ? prev : new Set<Endpoint>(["start", "end"]),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.start, value.end]);

  /** A sample decoded: record its pixel size as the line's frame. A line drawn
   *  on a different-sized frame is scaled proportionally so it stays on the
   *  same doorway (NVR sub-/main-stream exports share the field of view). A
   *  legacy line with no recorded size is kept as-is — and only bound to this
   *  frame if it actually fits. */
  const adoptFrame = (size: FrameSize) => {
    setFrame(size);
    const next: FrameSizeTuple = [size.width, size.height];
    if (sameSize(frameSize, next)) return;
    if (frameSize !== null && isDrawn(value)) {
      const sx = next[0] / frameSize[0];
      const sy = next[1] / frameSize[1];
      setRescaledFrom(frameSize);
      onChange(
        {
          ...value,
          start: clampPoint([value.start[0] * sx, value.start[1] * sy], size),
          end: clampPoint([value.end[0] * sx, value.end[1] * sy], size),
        },
        next,
      );
      return;
    }
    const fits = [value.start, value.end].every(
      (p) => p[0] <= size.width && p[1] <= size.height,
    );
    if (!isDrawn(value) || fits) onChange(value, next);
  };

  const setEndpoint = (which: Endpoint, p: Point) => {
    internalEdit.current = true;
    // Placing a point on this sample binds the line to the sample's size.
    onChange(
      { ...value, [which]: p },
      frame ? [frame.width, frame.height] : frameSize,
    );
    if (!placed.has(which)) setPlaced(new Set(placed).add(which));
  };

  /** Screen-pixel distance between a client position and a frame point. */
  const screenDistance = (
    ctx: MediaFrameContext,
    clientX: number,
    clientY: number,
    p: Point,
  ) => {
    const s = ctx.toScreen(p);
    return s ? Math.hypot(clientX - s[0], clientY - s[1]) : Infinity;
  };

  const overlay = (ctx: MediaFrameContext) => {
    const { k } = ctx;

    const onPointerDown = (e: ReactPointerEvent<SVGSVGElement>) => {
      if (e.button !== 0) return;
      const p = ctx.toFrame(e.clientX, e.clientY);
      if (!p) return;
      e.currentTarget.setPointerCapture(e.pointerId);

      // Grab an existing endpoint if the click lands on it; otherwise place the
      // next endpoint where the click landed (and let the user drag it).
      const hits: Endpoint[] = (["start", "end"] as Endpoint[]).filter(
        (which) =>
          placed.has(which) &&
          screenDistance(ctx, e.clientX, e.clientY, value[which]) <= HANDLE_HIT_PX,
      );
      const target: Endpoint = hits[0] ?? next;
      setEndpoint(target, p);
      setDragging(target);
    };

    const onPointerMove = (e: ReactPointerEvent<SVGSVGElement>) => {
      const p = ctx.toFrame(e.clientX, e.clientY);
      setHover(p);
      if (dragging && p) setEndpoint(dragging, p);
    };

    const onPointerUp = (e: ReactPointerEvent<SVGSVGElement>) => {
      if (!dragging) return;
      e.currentTarget.releasePointerCapture(e.pointerId);
      setNext(dragging === "start" ? "end" : "start");
      setDragging(null);
    };

    const { start, end, inward } = value;
    const hasLine = placed.has("start") && placed.has("end");
    const mid: Point = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
    const arrowLen =
      Math.min(ctx.frame.width, ctx.frame.height) * ARROW_LEN_FRAC;
    const [ix, iy] = inwardVector(inward);
    const arrowTip: Point = [mid[0] + ix * arrowLen, mid[1] + iy * arrowLen];

    return (
      <svg
        className={`absolute inset-0 h-full w-full touch-none ${dragging ? "cursor-grabbing" : "cursor-crosshair"}`}
        viewBox={`0 0 ${ctx.frame.width} ${ctx.frame.height}`}
        preserveAspectRatio="none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => setHover(null)}
      >
        <defs>
          <marker
            id="line-picker-arrowhead"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerUnits="userSpaceOnUse"
            markerWidth={14 * k}
            markerHeight={14 * k}
            orient="auto-start-reverse"
          >
            <path d="M0,0 L10,5 L0,10 z" fill="var(--accent)" />
          </marker>
        </defs>

        {hasLine && (
          <>
            {/* halo so the line reads on any footage */}
            <line
              x1={start[0]}
              y1={start[1]}
              x2={end[0]}
              y2={end[1]}
              stroke="rgba(0,0,0,0.55)"
              strokeWidth={6}
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1={start[0]}
              y1={start[1]}
              x2={end[0]}
              y2={end[1]}
              stroke="#ffd84d"
              strokeWidth={2.5}
              vectorEffect="non-scaling-stroke"
            />
            {/* inward arrow from the midpoint */}
            <line
              x1={mid[0]}
              y1={mid[1]}
              x2={arrowTip[0]}
              y2={arrowTip[1]}
              stroke="var(--accent)"
              strokeWidth={3}
              vectorEffect="non-scaling-stroke"
              markerEnd="url(#line-picker-arrowhead)"
            />
          </>
        )}

        {(["start", "end"] as Endpoint[]).map((which) => {
          const p = value[which];
          if (!placed.has(which)) return null;
          return (
            <g key={which} className="cursor-grab">
              <circle
                cx={p[0]}
                cy={p[1]}
                r={7 * k}
                fill={which === next && !dragging ? "#ffd84d" : "#fff"}
                stroke="#000"
                strokeWidth={1.5}
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={p[0]}
                y={p[1]}
                dy={-12 * k}
                textAnchor="middle"
                className={LABEL}
                fontSize={12 * k}
                strokeWidth={3 * k}
              >
                {which === "start" ? "A" : "B"}
              </text>
            </g>
          );
        })}

        {hover && !dragging && (
          <text
            x={hover[0]}
            y={hover[1]}
            dx={10 * k}
            dy={-10 * k}
            className={`${LABEL} font-medium font-mono`}
            fontSize={12 * k}
            strokeWidth={3 * k}
          >
            {hover[0]}, {hover[1]}
          </text>
        )}
      </svg>
    );
  };

  const lineIsOutsideFrame =
    frame !== null &&
    [value.start, value.end].some(
      (p) => p[0] > frame.width || p[1] > frame.height,
    );

  return (
    <div className="flex flex-col gap-2.5">
      <MediaFrameViewer
        openLabel="Open sample clip or frame"
        changeLabel="Change sample"
        emptyHint={
          <>
            Open a short clip or a screenshot from this camera to draw the line
            on it — an NVR export as it came (.dav, .264, .mp4) works. The
            file stays on your computer; a native export the browser cannot
            play is converted by this PC's service and not kept. Only the
            coordinates and the frame size are saved.
            {frameSize && isDrawn(value) && (
              <>
                <br />
                The saved line was drawn on a {frameSize[0]}×{frameSize[1]}{" "}
                frame; footage is resized to that before counting.
              </>
            )}
          </>
        }
        onOpen={() => {
          setRescaledFrom(null);
          setNext("start");
        }}
        onFrame={adoptFrame}
        overlay={overlay}
      />

      {rescaledFrom && frame && (
        <p className={ui.notice}>
          This sample is {frame.width}×{frame.height}; the line drawn on a{" "}
          {rescaledFrom[0]}×{rescaledFrom[1]} frame was scaled to match. Check it
          still sits on the threshold — once saved, footage is processed at{" "}
          {frame.width}×{frame.height}.
        </p>
      )}
      {lineIsOutsideFrame && (
        <p className={`${ui.notice} ${ui.noticeError}`}>
          The saved line lies outside this {frame?.width}×{frame?.height} frame
          and was drawn before frame sizes were recorded, so it cannot be
          scaled — redraw it on this sample or load a sample of the original
          resolution.
        </p>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold">
          <span className="text-muted">Inward (counts as entry):</span>
          {INWARD.map((d) => (
            <button
              key={d}
              type="button"
              className={`${ui.btnQuiet} ${d === value.inward ? ui.btnSelected : ""}`}
              onClick={() => onChange({ ...value, inward: d }, frameSize)}
            >
              {d === "up" && "↑ "}
              {d === "down" && "↓ "}
              {d === "left" && "← "}
              {d === "right" && "→ "}
              {d}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-muted">
          Click to place point A, then B, across the doorway threshold. Drag
          either handle to adjust. The arrow shows the direction a person walks
          to be counted as entering — pick the one that points into the store.
          People are anchored at their feet, so draw the line on the floor.
          The sample's pixel size is saved with the line and footage of any
          other resolution is resized to it before counting.
        </p>
      </div>
    </div>
  );
}
